/**
 * Czech § 12a: an announced discount has to be shown next to the lowest price the item was
 * sold at during the 30 days before it. The backend computes that number and sends it with
 * every price object - one value, already with VAT, in the same currency and scale as
 * `price`, so it renders beside it as it arrives and nothing is recalculated here.
 *
 * The field arrives as `lowest_price_30d` and the response interceptor camel-cases it into
 * `lowestPrice30D` - with a capital D, which is not what anyone types from memory. Reading
 * `lowestPrice30d` yields undefined, which this module reads as "no data" and which would
 * silently strip every discount from the site. Hence the constant.
 */
export const LOWEST_PRICE_FIELD = 'lowestPrice30D'

/**
 * @param {ProductPrice} productPrice
 * @return {number|null} null means "no data", never "no discount"
 */
export function lowestPrice30d(productPrice) {
  const value = productPrice && productPrice[LOWEST_PRICE_FIELD]

  return typeof value === 'number' ? value : null
}

/**
 * Whether the § 12a caption belongs on screen.
 *
 * Two conditions, and neither of them is a comparison against `price`. The journal records the
 * *declared* price - the base price minus a public sale, and nothing else - so a personal
 * reduction (loyalty percentage, professional pricing, a promocode) puts `price` below every
 * number the journal can hold, and a `lowest > price` test would hide the caption from exactly
 * those users permanently. For everyone else an announced sale enters the journal at the
 * recorder's next hourly pass, so `lowest > price` survives at most an hour past the start of a
 * sale: the low equalling the current price is the normal state, not a reason to drop the line.
 *
 * What is left is what the rule is actually about. A reduction has to be on screen, since the
 * caption accompanies a discount rather than standing on its own - `hasActivePromotion` is the
 * public sale, and `oldPrice` is added because the templates render their crossed-out price off
 * that field alone, so the caption cannot go missing underneath a discount that is displayed.
 * And the journal has to hold a number at all.
 *
 * @param {ProductPrice} productPrice
 * @return {boolean}
 */
export function showsLowestPrice(productPrice) {
  if (!productPrice) return false
  if (!productPrice.hasActivePromotion && !productPrice.oldPrice) return false

  return lowestPrice30d(productPrice) !== null
}

/**
 * Which caption to render. The claim "over the last 30 days" may only be made when the
 * journal actually reaches back that far - and it will not for the first month after the
 * mechanism goes live, so this is driven by the field and never by dates.
 *
 * @param {ProductPrice} productPrice
 * @return {'full'|'partial'}
 */
export function lowestPriceLabelKind(productPrice) {
  return productPrice && productPrice.hasFullPriceHistory ? 'full' : 'partial'
}
